/**
 * Color Theme Context - 컬러 테마 상태 관리
 */

import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';

export type ColorTheme = 'sage-green' | 'ocean-blue' | 'sunset-orange' | 'purple-night';

interface ColorThemeContextType {
  colorTheme: ColorTheme;
  setColorTheme: (colorTheme: ColorTheme) => void;
}

const ColorThemeContext = createContext<ColorThemeContextType | undefined>(undefined);

export const ColorThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // localStorage에서 컬러 테마 로드
  const [colorTheme, setColorThemeState] = useState<ColorTheme>(() => {
    const saved = localStorage.getItem('colorTheme') as ColorTheme | null;
    if (saved && ['sage-green', 'ocean-blue', 'sunset-orange', 'purple-night'].includes(saved)) {
      return saved;
    }
    return 'sage-green'; // 기본값: Sage Green
  });

  // 컬러 테마 변경 시 DOM에 속성 설정
  useEffect(() => {
    document.documentElement.setAttribute('data-color-theme', colorTheme);
    localStorage.setItem('colorTheme', colorTheme);
  }, [colorTheme]);

  const setColorTheme = (newColorTheme: ColorTheme) => {
    setColorThemeState(newColorTheme);
  };

  return (
    <ColorThemeContext.Provider value={{ colorTheme, setColorTheme }}>
      {children}
    </ColorThemeContext.Provider>
  );
};

export const useColorTheme = (): ColorThemeContextType => {
  const context = useContext(ColorThemeContext);
  if (!context) {
    throw new Error('useColorTheme must be used within ColorThemeProvider');
  }
  return context;
};
